import React, { useEffect, useRef, useState } from 'react'
import { Services } from '@/services/services';
import { SuperAdminEditMachineForm, SuperAdminInsertMachineForm, MachineFormData } from '@/components/forms/machines/Machine';
import { CurrentSession, SessionService } from '@/services/repository/session';
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout';
import { addServerErrors, onSubmit } from '@/components/forms/common/FormBase';
import { useForm, UseFormReturn } from 'react-hook-form';
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable';
import { formatDate } from '@/common/time';
import { isDesktopScreen, isLargeScreen, useResponsiveChange } from '@/common/device';
import { Doc } from '@/services/model/docs';
import { SimpleUser } from '../users/Users';
import { MachinePopupDocBody } from '@/popups/docs/docs';
import { MachinePopupQRCodeBody } from '@/popups/qrcode/qrcode';
import { useNavigate } from 'react-router-dom';
import { useModal } from '@/components/modal/Modal';
import { MachineMapComponent } from '@/components/Map/MachineMap';
import { BackButton } from '@/components/layout/NavBar';
import { removeEmptyFields } from '@/common/vars';
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu';
import { Translates } from '@/services/repository/i18n';
import { CardHistoryBody } from '@/pages/communal/machine/components/CardHistory';
import { MachineCreateWizard } from '@/popups/machines/wizard/create/Main';
import { useAppContext } from '@/context/app';
import { fetchCSV, fetchExcel } from '@/common/fetch';

export interface SimpleMachine {
  id: string
  imei: string

  manufacturer: string
  model: string
  serialNumber: string
  internalNumber: string
  machineTypeId: string
  name: string
}

export interface SuperAdminInsertPopupProps {
  services: Services,
  session: CurrentSession,
  onClose: () => void
  onInsert?: (data: { id: string, qr: string }) => void
}

export interface SuperAdminEditMachinesPopupProps {
  services: Services,
  session: CurrentSession,
  onClose: () => void
  machineId: string | undefined
}

export interface SuperAdminInsertMachinesPopupProps extends SuperAdminInsertPopupProps {
  defaultValues?: MachineFormData
}

export const SuperAdminEditMachinesPopup = ({ services, session, onClose, machineId }: SuperAdminEditMachinesPopupProps) => {
  const form = useForm<MachineFormData>()
  const [data, setData] = useState<MachineFormData>()

  useEffect(() => {
    if (machineId === undefined) {
      return
    }
    setData(undefined)
    services.session.fetchWithToken(`/api/superadmin/machines/byid/${machineId}`).then((r) => r.json()).then((data) => {
      setData(data)
      form.reset(data)
    })
  }, [machineId]);

  const handle = (data: MachineFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    return services.session.fetchWithToken(`/api/superadmin/machines/byid/${data.id}/update`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, session.translates, erros)
        return
      }
      onClose()
    })
  }

  return data !== undefined ? <SuperAdminEditMachineForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    formName='machine'
    readOnly={false}
  /> : <div className="spinner"></div>
}

export const SuperAdminInsertMachinesPopup = ({ services, session, onClose, onInsert, defaultValues }: SuperAdminInsertMachinesPopupProps) => {
  const form = useForm<MachineFormData>({
    defaultValues
  })

  const handle = (data: MachineFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    delete data.id
    return services.session.fetchWithToken(`/api/communal/machines/new`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, session.translates, erros)
        return
      }
      form.reset()
      onClose()
      if (onInsert) {
        onInsert(await r.json())
      }
    })
  }

  return <SuperAdminInsertMachineForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    formName='machine'
    readOnly={false}
  />
}

// const RelatedUsersPage = ({ services, session, machineId, onClose }: { services: Services, session: CurrentSession, machineId: string | undefined, onClose: () => void }) => {
//   // const [edited, setEdited] = useState<string>()
//   // const [inserted, setInserted] = useState<boolean>(false)
//   const searchTableRef = useRef<SearchTableRef>(null)

//   const tableParams: SearchTableParams<SimpleUser> = {
//     fetch: (q: PageQuery): Promise<PageResponse<SimpleUser>> => {
//       return services.session.fetchWithToken(`/api/superadmin/machines/byid/${machineId}/users/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
//         if (r.status === 200) {
//           return r.json()
//         }
//         return Promise.reject()
//       })
//     },
//     pageQuery: {
//       pageNumber: 1,
//       pageSize: 30,
//       search: ""
//     },
//     toolbox: () => <></>,
//     header: () => <>
//       <div className='hcol'></div>
//       <div className='hcol desktop-only'>{session.translates.for('elements.table.users.createdAt')}</div>
//       <div className='hcol desktop-only'>{session.translates.for('elements.table.users.email')}</div>
//       <div className='hcol'>{session.translates.for('elements.table.users.username')}</div>
//     </>,
//     row: (row, state, setState) => <>
//       <div className='col icocol' onClick={() => {
//         setState((state) => {
//           if (state === undefined) {
//             return
//           }
//           state.data = state.data?.map(item =>
//             item.id === row.id ? { ...item, isRelated: undefined } : item
//           )
//           return { ...state }
//         })

//         services.session.fetchWithToken(`/api/superadmin/machines/byid/${machineId}/relations/users/${row.id}`, {
//           method: 'POST',
//           headers: {
//             'Accept': 'application/json',
//             'Content-Type': 'application/json'
//           },
//           body: JSON.stringify({
//             id: row.id,
//             related: !row.isRelated
//           }),
//         }).then((resp) => {
//           setState((state) => {
//             if (state === undefined) {
//               return
//             }
//             state.data = state.data?.map(item =>
//               item.id === row.id ? { ...item, isRelated: resp.ok ? !row.isRelated : row.isRelated } : item
//             )
//             return { ...state }
//           })
//         })

//       }}>
//         {row.isRelated === undefined ? <div className='spinner'></div> : row.isRelated ? <i className='svgfont-checkbox' /> : <i className='svgfont-checkbox-blank' />}
//       </div>
//       <div className='col desktop-only'>{formatDate(new Date(row.createdAt))}</div>
//       <div className='col desktop-only'>{row.email}</div>
//       <div className='col'>{row.username}</div>
//     </>,
//     translates: session.translates
//   }

//   return <SearchTable ref={searchTableRef} {...tableParams} />
// }

interface SimpleUserGroup {
  id: string
  name: string
  isRelated?: boolean
}

const RelatedUserGroupsPage = ({
  services,
  session,
  machineId,
  onClose,
  title
}: {
  services: Services,
  session: CurrentSession,
  machineId: string | undefined,
  onClose: () => void,
  title: string
}) => {
  const searchTableRef = useRef<SearchTableRef>(null)

  const tableParams: SearchTableParams<SimpleUserGroup> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUserGroup>> => {
      return services.session.fetchWithToken(
        `/api/superadmin/machines/byid/${machineId}/usergroups/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`
      ).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <></>,
    header: () => <>
      <div className='hcol'></div>
      <div className='hcol'>{session.translates.for('elements.table.usergroups.name')}</div>
    </>,
    row: (row, state, setState) => <>
      <div className='col icocol' onClick={() => {
        setState((state) => {
          if (state === undefined) return
          state.data = state.data?.map(item =>
            item.id === row.id ? { ...item, isRelated: undefined } : item
          )
          return { ...state }
        })

        services.session.fetchWithToken(`/api/superadmin/machines/byid/${machineId}/relations/usergroups/${row.id}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            related: !row.isRelated
          }),
        }).then((resp) => {
          setState((state) => {
            if (state === undefined) return
            state.data = state.data?.map(item =>
              item.id === row.id ? { ...item, isRelated: resp.ok ? !row.isRelated : row.isRelated } : item
            )
            return { ...state }
          })
        })

      }}>
        {row.isRelated === undefined
          ? <div className='spinner'></div>
          : row.isRelated
            ? <i className='svgfont-checkbox' />
            : <i className='svgfont-checkbox-blank' />}
      </div>
      <div className='col'>{row.name}</div>
    </>,
    translates: session.translates
  }

  return <>
    {title ? <h3>{title}</h3> : null}
    <SearchTable ref={searchTableRef} {...tableParams} />
  </>
}

export const SuperAdminMachineMapPopup = ({ services, session, onClose, imei, title }: {
  services: Services,
  session: CurrentSession,
  onClose: () => void
  imei: string,
  title?: string
}) => {
  return <>
    {title ? <h3>{title}</h3> : null}
    <MachineMapComponent services={services} session={session} imei={imei} />
  </>
}

const HistoryExportPopup = ({ machine, onClose }: {
  machine: SimpleMachine,
  onClose: () => void
}) => {
  const { services, session, translates } = useAppContext()
  const title = `${machine.manufacturer} - ${machine.model} - ${machine.internalNumber}`

  const menu: Menu = {
    title: title + ' : ' + translates?.for("popup.export.machine_history_export_title"),
    rows: [
      {
        title: 'popup.export.excel_export',
        action: () => fetchExcel({
          services,
          url: `/api/superadmin/machines/byid/${machine.id}/history/excel`,
          fileName: `${machine.manufacturer}_${machine.model}_${machine.internalNumber}_history.xlsx`
        })
      },
      {
        title: 'popup.export.csv_export',
        action: () => fetchCSV({
          services,
          url: `/api/superadmin/machines/byid/${machine.id}/history/csv`,
          fileName: `${machine.manufacturer}_${machine.model}_${machine.internalNumber}_history.csv`
        })
      }
    ]
  }

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

const HitsExportPopup = ({ machine, onClose }: {
  machine: SimpleMachine,
  onClose: () => void
}) => {
  const { services, session, translates } = useAppContext()
  const title = `${machine.manufacturer} - ${machine.model} - ${machine.internalNumber}`

  const menu: Menu = {
    title: title + ' : ' + translates?.for("popup.export.machine_hits_export_title"),
    rows: [
      {
        title: 'popup.export.excel_export',
        action: () => fetchExcel({
          services,
          url: `/api/superadmin/machines/byid/${machine.id}/hits/excel`,
          fileName: `${machine.manufacturer}_${machine.model}_${machine.internalNumber}_hits.xlsx`
        })
      },
      {
        title: 'popup.export.csv_export',
        action: () => fetchCSV({
          services,
          url: `/api/superadmin/machines/byid/${machine.id}/hits/csv`,
          fileName: `${machine.manufacturer}_${machine.model}_${machine.internalNumber}_hits.csv`
        })
      }
    ]
  }

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

export const SuperAdminMachineMenuPopup = ({ services, session, translates, onClose, row }: {
  translates: Translates,
  services: Services,
  session: CurrentSession,
  onClose: () => void
  row: SimpleMachine
}) => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const title = `${row.manufacturer || ''} ${row.model || ''} ${row.internalNumber ? row.internalNumber : row.serialNumber || ''}`
  const menu: Menu = {
    title,
    rows: [
      {
        icon: 'svgfont-link',
        title: 'elements.table.machines.menu.edit.open_machine_page',
        action: () => navigate(`/communal/machine/${row.id}`)
      },
      {
        icon: 'svgfont-user-card',
        title: 'elements.table.machines.menu.edit.show_card_history',
        action: () => openModal((props) => CardHistoryBody({ ...props, machineId: row.id, services, session, title }))
      },
      {
        icon: 'svgfont-qrcode',
        title: 'elements.table.machines.menu.edit.show_qr_code',
        action: () => openModal((props) => MachinePopupQRCodeBody({ ...props, machineId: row.id, services, session, translates: session.translates, title }))
      },
      {
        icon: row.machineTypeId ? 'svgfont-documentation' : '-',
        title: 'elements.table.machines.menu.edit.show_docs',
        action: () => row.machineTypeId && openModal((props) => MachinePopupDocBody({ ...props, machineTypeId: row.machineTypeId, services, session, translates: session.translates, title }))
      },
      {
        icon: 'svgfont-users',
        title: 'elements.table.machines.menu.edit.related_user_groups',
        action: () => openModal((props) => RelatedUserGroupsPage({ ...props, machineId: row.id, services, session, title }))
      },
      ...(row.imei ? [{
        icon: 'svgfont-map',
        title: 'elements.table.machines.menu.edit.machine_map',
        action: () => openModal((props) => SuperAdminMachineMapPopup({ ...props, imei: row.imei, services, session, title }))
      },
      {
        icon: 'svgfont-calendar',
        title: 'elements.table.machines.menu.edit.pathmap',
        action: () => navigate(`/communal/machine/${row.id}/pathmap`)
      }] : []),
      {
        icon: 'svgfont-edit',
        title: 'elements.table.machines.menu.edit.edit_machine',
        action: () => openModal((props) => SuperAdminEditMachinesPopup({ ...props, machineId: row.id, services, session }))
      },
      ...(row.imei ? [{
        icon: 'svgfont-csv',
        title: 'elements.table.machines.menu.edit.history_export',
        action: () => openModal((props) => HistoryExportPopup({ ...props, machine: row }))
      },
      {
        icon: 'svgfont-csv',
        title: 'elements.table.machines.menu.edit.hits_export',
        action: () => openModal((props) => HitsExportPopup({ ...props, machine: row }))
      }] : [])
    ]
  }

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

const ExportPopup = ({ onClose,  }: {
  onClose: () => void
}) => {
  const { services, session } = useAppContext()

  const menu: Menu = {
    title: "popup.export.machines_export_title",
    rows: [
      {
        title: 'popup.export.excel_export',
        action: () => fetchExcel({
          services,
          url: '/api/communal/my/machines/excel',
          fileName: 'machines.xlsx'
        })
      },
      {
        title: 'popup.export.csv_export',
        action: () => fetchCSV({
          services,
          url: '/api/communal/my/machines/csv',
          fileName: 'machines.csv'
        })
      }
    ]
  }

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

export const SuperAdminMachinesPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()

  useResponsiveChange()

  const tableParams: SearchTableParams<SimpleMachine> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleMachine>> => {
      return services.session.fetchWithToken(`/api/superadmin/machines/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => MachineCreateWizard({
        ...props, onDone: (machineId: string) => {
          // navigate(`/communal/machine/${machineId}`)
          searchTableRef.current?.refresh()
          window.open(`/communal/machine/${machineId}`, "_blank", "noopener,noreferrer");
          props.onClose()
        }
      }))}>
        <i className='svgfont-add' />
      </div>
      <div className='tool' onClick={() => openModal((props) => ExportPopup({ ...props }))}>
        <img src="/public/img/icons/export-file.svg" />
      </div>
    </>,
    header: () => <>
      <div className='hcol'>{session.translates.for('elements.table.machines.manufacturer')}</div>
      {/* <div className='hcol'>{session.translates.for('elements.table.machines.type_category')}</div> */}
      <div className='hcol'>{session.translates.for('elements.table.machines.model')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.number')}</div>
      {/* <div className='hcol'>{session.translates.for('elements.table.machines.name')}</div> */}
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col'>{row.manufacturer}</div>
      {/* <div className='col'>{row.}</div> */}
      <div className='col'>{row.model}</div>
      <div className='col'>{row.internalNumber ? row.internalNumber : row.serialNumber || row.id}</div>
      {/* <div className='col'>{row.name}</div> */}
      <div className='col icocol' onClick={() => openModal((props) => SuperAdminMachineMenuPopup({ ...props, row: row, services, session, translates: session.translates }))}>
        <i className='svgfont-menu' />
      </div>
    </>,
    translates: session.translates
  }

  return <LoggedInPageLayout pageName='superadmin-machines' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}
